import React, { useState, useEffect } from 'react';
import {
  Users,
  Search,
  Plus,
  Phone,
  Mail,
  Building2,
  MapPin,
  Edit2,
  Trash2,
  X,
  User,
  Star,
  StarOff,
  Clock,
  PlayCircle,
  StopCircle,
  Coffee,
  Circle,
  LayoutGrid,
  List,
  TableProperties
} from 'lucide-react';
import { getFichadorStatus, fichadorClockIn, fichadorClockOut, fichadorBreakStart, fichadorBreakEnd } from '../../services/api';

// Tabs del panel
const TABS = [
  { id: 'equipo', label: 'Equipo', icon: Users },
  { id: 'contactos', label: 'Contactos', icon: Building2 },
  { id: 'fichador', label: 'Fichador', icon: Clock }
];

// Categorías de contactos
const CATEGORIES = [
  { id: 'all', label: 'Todos', color: 'gray' },
  { id: 'cliente', label: 'Clientes', color: 'blue' },
  { id: 'proveedor', label: 'Proveedores', color: 'green' },
  { id: 'empleado', label: 'Empleados', color: 'purple' },
  { id: 'otro', label: 'Otros', color: 'orange' }
];

// Opciones de vista
const VIEW_MODES = [
  { id: 'grid', icon: LayoutGrid, label: 'Cuadrícula' },
  { id: 'list', icon: List, label: 'Lista' },
  { id: 'compact', icon: TableProperties, label: 'Compacta' }
];

export default function ContactosPanel({ user }) {
  const [activeTab, setActiveTab] = useState('equipo');
  const [contacts, setContacts] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [teamSearchTerm, setTeamSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showNewContact, setShowNewContact] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // grid, list, compact

  // Fichador state (sincronizado con API)
  const [isClockedIn, setIsClockedIn] = useState(false);
  const [clockInTime, setClockInTime] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [breakMode, setBreakMode] = useState(false);
  const [todayRecords, setTodayRecords] = useState([]);
  const [fichadorLoading, setFichadorLoading] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    category: 'cliente',
    address: '',
    notes: '',
    favorite: false
  });

  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Load team members
  useEffect(() => {
    const loadTeam = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch('/api/users/list', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          // Mark some as online randomly for demo
          const members = (data.users || data || []).map((u, i) => ({
            ...u,
            status: i < 3 ? 'online' : i < 5 ? 'away' : 'offline',
            department: u.department || 'General'
          }));
          setTeamMembers(members);
        }
      } catch (error) {
        console.error('Error loading team:', error);
        // Fallback mock data
        setTeamMembers([
          { id: 1, name: 'Xavi', email: 'xavi@manantial.com', role: 'superadmin', status: 'online', department: 'Gerencia' },
          { id: 2, name: 'Carlos García', email: 'carlos@manantial.com', role: 'usuario', status: 'online', department: 'Comercial' },
          { id: 3, name: 'María López', email: 'maria@manantial.com', role: 'usuario', status: 'away', department: 'Administracion' },
          { id: 4, name: 'Juan Pérez', email: 'juan@manantial.com', role: 'tecnico_externo', status: 'offline', department: 'SAT' },
        ]);
      }
    };
    loadTeam();
  }, []);

  // Cargar contactos
  useEffect(() => {
    setContacts([
      { id: 1, name: 'Teatro Real', company: 'Teatro Real', email: 'contacto@teatroreal.es', phone: '+34 612 345 678', category: 'cliente', address: 'Madrid', favorite: true },
      { id: 2, name: 'Cine Comedia', company: 'Cine Comedia', email: 'info@cinecomedia.com', phone: '+34 623 456 789', category: 'cliente', address: 'Barcelona', favorite: false },
      { id: 3, name: 'Ingenico España', company: 'Ingenico', email: 'soporte@ingenico.es', phone: '+34 900 123 456', category: 'proveedor', address: 'Madrid', favorite: true },
    ]);
  }, []);

  // Cargar estado fichador desde API
  useEffect(() => {
    const loadFichadorStatus = async () => {
      try {
        const res = await getFichadorStatus();
        if (res.data.success) {
          setIsClockedIn(res.data.status === 'working' || res.data.status === 'break');
          setBreakMode(res.data.status === 'break');
          if (res.data.clockInTime) {
            setClockInTime(new Date(res.data.clockInTime));
          }
          if (res.data.records) {
            setTodayRecords(res.data.records.map(r => ({
              type: r.type,
              time: new Date(r.time)
            })));
          }
        }
      } catch (err) {
        console.error('Error cargando fichador:', err);
      }
    };
    loadFichadorStatus();
    // Recargar cada 30 segundos para sincronizar
    const interval = setInterval(loadFichadorStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  // Fichador functions - usando API
  const handleClockIn = async () => {
    setFichadorLoading(true);
    try {
      const res = await fichadorClockIn();
      if (res.data.success) {
        const now = new Date();
        setClockInTime(now);
        setIsClockedIn(true);
        setBreakMode(false);
        setTodayRecords(prev => [...prev, { type: 'entrada', time: now }]);
      }
    } catch (err) {
      console.error('Error fichando entrada:', err);
    }
    setFichadorLoading(false);
  };

  const handleClockOut = async () => {
    setFichadorLoading(true);
    try {
      const res = await fichadorClockOut();
      if (res.data.success) {
        const now = new Date();
        setIsClockedIn(false);
        setClockInTime(null);
        setBreakMode(false);
        setTodayRecords(prev => [...prev, { type: 'salida', time: now }]);
      }
    } catch (err) {
      console.error('Error fichando salida:', err);
    }
    setFichadorLoading(false);
  };

  const handleBreak = async () => {
    setFichadorLoading(true);
    try {
      const now = new Date();
      if (breakMode) {
        const res = await fichadorBreakEnd();
        if (res.data.success) {
          setBreakMode(false);
          setTodayRecords(prev => [...prev, { type: 'fin_descanso', time: now }]);
        }
      } else {
        const res = await fichadorBreakStart('descanso');
        if (res.data.success) {
          setBreakMode(true);
          setTodayRecords(prev => [...prev, { type: 'inicio_descanso', time: now }]);
        }
      }
    } catch (err) {
      console.error('Error en descanso:', err);
    }
    setFichadorLoading(false);
  };

  // Estado para filtro de fichajes
  const [fichadorSearch, setFichadorSearch] = useState('');

  // Función legacy para compatibilidad (ya no se usa el if/else original)
  const handleBreakLegacy = () => {
    const now = new Date();
    if (breakMode) {
      setBreakMode(false);
      setTodayRecords(prev => [...prev, { type: 'fin_descanso', time: now }]);
    } else {
      setBreakMode(true);
      setTodayRecords(prev => [...prev, { type: 'inicio_descanso', time: now }]);
    }
  };

  const getElapsedTime = () => {
    if (!clockInTime) return '00:00:00';
    const diff = currentTime - clockInTime;
    const hours = Math.floor(diff / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const filteredContacts = contacts.filter(contact => {
    const matchesSearch =
      contact.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || contact.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const sortedContacts = [...filteredContacts].sort((a, b) => {
    if (a.favorite && !b.favorite) return -1;
    if (!a.favorite && b.favorite) return 1;
    return a.name.localeCompare(b.name);
  });

  const resetForm = () => {
    setFormData({
      name: '',
      company: '',
      email: '',
      phone: '',
      category: 'cliente',
      address: '',
      notes: '',
      favorite: false
    });
  };

  const handleSaveContact = () => {
    if (!formData.name.trim()) {
      alert('El nombre es obligatorio');
      return;
    }
    if (editingContact) {
      setContacts(prev => prev.map(c => c.id === editingContact.id ? { ...formData, id: editingContact.id } : c));
    } else {
      setContacts(prev => [...prev, { ...formData, id: Date.now() }]);
    }
    setShowNewContact(false);
    setEditingContact(null);
    resetForm();
  };

  const handleEditContact = (contact) => {
    setFormData(contact);
    setEditingContact(contact);
    setShowNewContact(true);
  };

  const handleDeleteContact = (id) => {
    if (window.confirm('¿Eliminar este contacto?')) {
      setContacts(prev => prev.filter(c => c.id !== id));
    }
  };

  const toggleFavorite = (id) => {
    setContacts(prev => prev.map(c => c.id === id ? { ...c, favorite: !c.favorite } : c));
  };

  const getCategoryColor = (category) => {
    const colors = {
      blue: 'bg-blue-100 text-blue-700',
      green: 'bg-green-100 text-green-700',
      purple: 'bg-purple-100 text-purple-700',
      orange: 'bg-orange-100 text-orange-700',
      gray: 'bg-gray-100 text-gray-700'
    };
    const cat = CATEGORIES.find(c => c.id === category);
    return colors[cat?.color] || colors.gray;
  };

  // Verificar si estamos en horario laboral (Lunes-Viernes 9:00-18:00)
  const isWorkingHours = () => {
    const now = new Date();
    const day = now.getDay(); // 0=Domingo, 1=Lunes...
    const hour = now.getHours();
    // Lunes a Viernes (1-5), de 9:00 a 18:00
    return day >= 1 && day <= 5 && hour >= 9 && hour < 18;
  };

  const inWorkHours = isWorkingHours();

  const getStatusColor = (status) => {
    if (!inWorkHours) {
      // Fuera de horario: gris para todos
      return 'bg-gray-400';
    }
    // En horario laboral
    switch (status) {
      case 'online': return 'bg-green-500'; // Conectado = verde
      case 'away': return 'bg-yellow-500'; // Ausente = amarillo
      default: return 'bg-red-500'; // Desconectado en horario = rojo
    }
  };

  const getStatusLabel = (status) => {
    if (!inWorkHours) {
      return 'Fuera de horario';
    }
    switch (status) {
      case 'online': return 'Conectado';
      case 'away': return 'Ausente';
      default: return 'Desconectado';
    }
  };

  // Render Equipo tab
  const renderEquipo = () => {
    // Filtrar por búsqueda
    const filteredTeam = teamMembers.filter(m =>
      m.name?.toLowerCase().includes(teamSearchTerm.toLowerCase()) ||
      m.email?.toLowerCase().includes(teamSearchTerm.toLowerCase()) ||
      m.department?.toLowerCase().includes(teamSearchTerm.toLowerCase())
    );

    const onlineMembers = filteredTeam.filter(m => m.status === 'online');
    const awayMembers = filteredTeam.filter(m => m.status === 'away');
    const offlineMembers = filteredTeam.filter(m => m.status === 'offline');

    return (
      <div className="space-y-4 md:space-y-6">
        {/* Buscador de equipo - Responsive */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-4">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar en el equipo..."
              value={teamSearchTerm}
              onChange={(e) => setTeamSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#3B82F6]"
            />
          </div>
          <span className="text-xs sm:text-sm text-gray-500 text-center sm:text-left">
            {filteredTeam.length} miembro{filteredTeam.length !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Indicador de horario - Responsive */}
        <div className={`p-2.5 md:p-3 rounded-lg flex flex-wrap items-center gap-2 md:gap-3 ${inWorkHours ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'}`}>
          <div className={`w-2.5 h-2.5 md:w-3 md:h-3 rounded-full flex-shrink-0 ${inWorkHours ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`}></div>
          <span className={`text-xs md:text-sm font-medium ${inWorkHours ? 'text-green-700' : 'text-gray-600'}`}>
            {inWorkHours ? 'Horario laboral (9-18h)' : 'Fuera de horario'}
          </span>
          <span className="text-xs text-gray-500 ml-auto">
            {currentTime.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
          </span>
        </div>

        {/* Online */}
        <div>
          <h3 className="text-xs md:text-sm font-medium text-gray-500 mb-2 md:mb-3 flex items-center gap-2">
            <Circle className="w-2.5 h-2.5 md:w-3 md:h-3 fill-green-500 text-green-500" />
            Conectados ({onlineMembers.length})
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 md:gap-3">
            {onlineMembers.map(member => (
              <div key={member.id} className="bg-white rounded-lg border border-gray-200 p-3 md:p-4 hover:shadow-md transition-shadow active:scale-[0.98]">
                <div className="flex items-center gap-2 md:gap-3">
                  <div className="relative flex-shrink-0">
                    <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center text-white font-bold text-sm md:text-base">
                      {member.name?.charAt(0).toUpperCase()}
                    </div>
                    <span className={`absolute bottom-0 right-0 w-3 h-3 md:w-3.5 md:h-3.5 rounded-full border-2 border-white ${getStatusColor(member.status)}`}></span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate text-sm md:text-base">{member.name}</p>
                    <p className="text-xs md:text-sm text-gray-500 truncate">{member.department}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Away */}
        {awayMembers.length > 0 && (
          <div>
            <h3 className="text-xs md:text-sm font-medium text-gray-500 mb-2 md:mb-3 flex items-center gap-2">
              <Circle className="w-2.5 h-2.5 md:w-3 md:h-3 fill-yellow-500 text-yellow-500" />
              Ausentes ({awayMembers.length})
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 md:gap-3">
              {awayMembers.map(member => (
                <div key={member.id} className="bg-white rounded-lg border border-gray-200 p-3 md:p-4 opacity-80">
                  <div className="flex items-center gap-2 md:gap-3">
                    <div className="relative flex-shrink-0">
                      <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center text-white font-bold text-sm md:text-base">
                        {member.name?.charAt(0).toUpperCase()}
                      </div>
                      <span className={`absolute bottom-0 right-0 w-3 h-3 md:w-3.5 md:h-3.5 rounded-full border-2 border-white ${getStatusColor(member.status)}`}></span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate text-sm md:text-base">{member.name}</p>
                      <p className="text-xs md:text-sm text-gray-500 truncate">{member.department}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Offline */}
        {offlineMembers.length > 0 && (
          <div>
            <h3 className="text-xs md:text-sm font-medium text-gray-500 mb-2 md:mb-3 flex items-center gap-2">
              <Circle className="w-2.5 h-2.5 md:w-3 md:h-3 fill-gray-400 text-gray-400" />
              Desconectados ({offlineMembers.length})
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 md:gap-3">
              {offlineMembers.map(member => (
                <div key={member.id} className="bg-white rounded-lg border border-gray-200 p-3 md:p-4 opacity-60">
                  <div className="flex items-center gap-2 md:gap-3">
                    <div className="relative flex-shrink-0">
                      <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-gray-300 to-gray-500 rounded-full flex items-center justify-center text-white font-bold text-sm md:text-base">
                        {member.name?.charAt(0).toUpperCase()}
                      </div>
                      <span className={`absolute bottom-0 right-0 w-3 h-3 md:w-3.5 md:h-3.5 rounded-full border-2 border-white ${getStatusColor(member.status)}`}></span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate text-sm md:text-base">{member.name}</p>
                      <p className="text-xs md:text-sm text-gray-500 truncate">{member.department}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render Contactos tab
  const renderContactos = () => (
    <div className="space-y-3 md:space-y-4">
      {/* Filters - Responsive */}
      <div className="space-y-3">
        {/* Row 1: Search + View Toggle + New Button */}
        <div className="flex items-center gap-2 md:gap-4">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar contactos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#3B82F6]"
            />
          </div>

          {/* View Mode Toggle */}
          <div className="flex items-center gap-0.5 bg-gray-100 rounded-lg p-1 flex-shrink-0">
            {VIEW_MODES.map(mode => {
              const Icon = mode.icon;
              return (
                <button
                  key={mode.id}
                  onClick={() => setViewMode(mode.id)}
                  className={`p-1.5 md:p-2 rounded-md transition-colors ${
                    viewMode === mode.id
                      ? 'bg-white text-[#3B82F6] shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                  title={mode.label}
                >
                  <Icon className="w-4 h-4" />
                </button>
              );
            })}
          </div>

          <button
            onClick={() => { resetForm(); setEditingContact(null); setShowNewContact(true); }}
            className="flex items-center gap-1.5 md:gap-2 px-3 md:px-4 py-2.5 bg-[#3B82F6] text-white rounded-lg hover:bg-[#2563EB] transition-colors text-sm flex-shrink-0"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Nuevo</span>
          </button>
        </div>

        {/* Row 2: Category filters - Scrollable on mobile */}
        <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide -mx-3 px-3 md:mx-0 md:px-0">
          {CATEGORIES.map(cat => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`px-3 py-1.5 rounded-full text-xs md:text-sm font-medium transition-colors whitespace-nowrap flex-shrink-0 ${
                selectedCategory === cat.id
                  ? 'bg-[#3B82F6] text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {cat.label}
            </button>
          ))}
          <span className="text-xs text-gray-400 ml-2 whitespace-nowrap">
            {sortedContacts.length} resultado{sortedContacts.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* Contacts Display */}
      {sortedContacts.length === 0 ? (
        <div className="text-center py-8 md:py-12 text-gray-400">
          <Building2 className="w-12 h-12 md:w-16 md:h-16 mx-auto mb-3 md:mb-4 opacity-50" />
          <p className="text-sm md:text-base">No hay contactos</p>
        </div>
      ) : viewMode === 'grid' ? (
        /* Vista Cuadrícula - Responsive */
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4">
          {sortedContacts.map(contact => (
            <div key={contact.id} className="bg-white rounded-xl border border-gray-200 p-3 md:p-4 hover:shadow-lg transition-shadow active:scale-[0.98]">
              <div className="flex items-start justify-between mb-2 md:mb-3">
                <div className="flex items-center gap-2 md:gap-3 min-w-0">
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-[#3B82F6] to-[#8B5CF6] rounded-full flex items-center justify-center text-white font-bold text-sm md:text-base flex-shrink-0">
                    {contact.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-semibold text-gray-900 text-sm md:text-base truncate">{contact.name}</h3>
                    <p className="text-xs md:text-sm text-gray-500 truncate">{contact.company}</p>
                  </div>
                </div>
                <button onClick={() => toggleFavorite(contact.id)} className="p-1 hover:bg-gray-100 rounded flex-shrink-0">
                  {contact.favorite ? <Star className="w-4 h-4 md:w-5 md:h-5 text-yellow-500 fill-yellow-500" /> : <StarOff className="w-4 h-4 md:w-5 md:h-5 text-gray-300" />}
                </button>
              </div>
              <div className="space-y-1.5 md:space-y-2 mb-2 md:mb-3">
                <div className="flex items-center gap-2 text-xs md:text-sm text-gray-600">
                  <Mail className="w-3.5 h-3.5 md:w-4 md:h-4 text-gray-400 flex-shrink-0" />
                  <a href={`mailto:${contact.email}`} className="hover:text-[#3B82F6] truncate">{contact.email}</a>
                </div>
                <div className="flex items-center gap-2 text-xs md:text-sm text-gray-600">
                  <Phone className="w-3.5 h-3.5 md:w-4 md:h-4 text-gray-400 flex-shrink-0" />
                  <a href={`tel:${contact.phone}`} className="hover:text-[#3B82F6]">{contact.phone}</a>
                </div>
              </div>
              <div className="flex items-center justify-between pt-2 md:pt-3 border-t border-gray-100">
                <span className={`px-2 py-0.5 md:px-2.5 md:py-1 rounded-full text-[10px] md:text-xs font-medium ${getCategoryColor(contact.category)}`}>
                  {CATEGORIES.find(c => c.id === contact.category)?.label}
                </span>
                <div className="flex items-center gap-0.5 md:gap-1">
                  <button onClick={() => handleEditContact(contact)} className="p-1.5 hover:bg-gray-100 rounded-lg">
                    <Edit2 className="w-3.5 h-3.5 md:w-4 md:h-4 text-gray-400" />
                  </button>
                  <button onClick={() => handleDeleteContact(contact.id)} className="p-1.5 hover:bg-red-50 rounded-lg">
                    <Trash2 className="w-3.5 h-3.5 md:w-4 md:h-4 text-red-400" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : viewMode === 'list' ? (
        /* Vista Lista */
        <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
          {sortedContacts.map(contact => (
            <div key={contact.id} className="p-4 hover:bg-gray-50 transition-colors flex items-center gap-4">
              <div className="w-10 h-10 bg-gradient-to-br from-[#3B82F6] to-[#8B5CF6] rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                {contact.name.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-gray-900">{contact.name}</h3>
                  {contact.favorite && <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />}
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getCategoryColor(contact.category)}`}>
                    {CATEGORIES.find(c => c.id === contact.category)?.label}
                  </span>
                </div>
                <p className="text-sm text-gray-500">{contact.company}</p>
              </div>
              <div className="hidden md:flex items-center gap-6 text-sm text-gray-600">
                <a href={`mailto:${contact.email}`} className="flex items-center gap-1.5 hover:text-[#3B82F6]">
                  <Mail className="w-4 h-4" />
                  <span className="truncate max-w-[180px]">{contact.email}</span>
                </a>
                <a href={`tel:${contact.phone}`} className="flex items-center gap-1.5 hover:text-[#3B82F6]">
                  <Phone className="w-4 h-4" />
                  {contact.phone}
                </a>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => toggleFavorite(contact.id)} className="p-1.5 hover:bg-gray-100 rounded-lg">
                  {contact.favorite ? <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" /> : <StarOff className="w-4 h-4 text-gray-300" />}
                </button>
                <button onClick={() => handleEditContact(contact)} className="p-1.5 hover:bg-gray-100 rounded-lg">
                  <Edit2 className="w-4 h-4 text-gray-400" />
                </button>
                <button onClick={() => handleDeleteContact(contact.id)} className="p-1.5 hover:bg-red-50 rounded-lg">
                  <Trash2 className="w-4 h-4 text-red-400" />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* Vista Compacta (Tabla) */
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Nombre</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">Empresa</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden lg:table-cell">Email</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden sm:table-cell">Teléfono</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Categoría</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sortedContacts.map(contact => (
                <tr key={contact.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {contact.favorite && <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />}
                      <span className="font-medium text-gray-900">{contact.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 hidden md:table-cell">{contact.company}</td>
                  <td className="px-4 py-3 hidden lg:table-cell">
                    <a href={`mailto:${contact.email}`} className="text-sm text-gray-600 hover:text-[#3B82F6]">{contact.email}</a>
                  </td>
                  <td className="px-4 py-3 hidden sm:table-cell">
                    <a href={`tel:${contact.phone}`} className="text-sm text-gray-600 hover:text-[#3B82F6]">{contact.phone}</a>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getCategoryColor(contact.category)}`}>
                      {CATEGORIES.find(c => c.id === contact.category)?.label}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => toggleFavorite(contact.id)} className="p-1 hover:bg-gray-100 rounded">
                        {contact.favorite ? <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" /> : <StarOff className="w-4 h-4 text-gray-300" />}
                      </button>
                      <button onClick={() => handleEditContact(contact)} className="p-1 hover:bg-gray-100 rounded">
                        <Edit2 className="w-4 h-4 text-gray-400" />
                      </button>
                      <button onClick={() => handleDeleteContact(contact.id)} className="p-1 hover:bg-red-50 rounded">
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  // Filtrar registros por búsqueda
  const filteredRecords = todayRecords.filter(record => {
    if (!fichadorSearch.trim()) return true;
    const search = fichadorSearch.toLowerCase();
    const typeText = record.type === 'entrada' ? 'entrada' :
                     record.type === 'salida' ? 'salida' :
                     record.type === 'inicio_descanso' ? 'inicio descanso' :
                     record.type === 'fin_descanso' ? 'fin descanso' : '';
    const timeText = record.time.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
    const dateText = record.time.toLocaleDateString('es-ES');
    return typeText.includes(search) || timeText.includes(search) || dateText.includes(search);
  });

  // Render Fichador tab
  const renderFichador = () => (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Current Time Card */}
      <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center">
        <p className="text-gray-500 mb-2">{currentTime.toLocaleDateString('es-ES', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
        <p className="text-6xl font-bold text-gray-900 font-mono">
          {currentTime.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
        </p>

        {isClockedIn && (
          <div className="mt-6">
            <p className="text-sm text-gray-500">Tiempo trabajado hoy</p>
            <p className="text-3xl font-bold text-[#3B82F6] font-mono">{getElapsedTime()}</p>
            {breakMode && <p className="text-sm text-yellow-600 mt-1">☕ En descanso</p>}
          </div>
        )}

        <div className="flex items-center justify-center gap-4 mt-8">
          {!isClockedIn ? (
            <button
              onClick={handleClockIn}
              disabled={fichadorLoading}
              className="flex items-center gap-3 px-8 py-4 bg-green-500 text-white rounded-xl hover:bg-green-600 transition-colors text-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <PlayCircle className="w-6 h-6" />
              {fichadorLoading ? 'Cargando...' : 'Fichar Entrada'}
            </button>
          ) : (
            <>
              <button
                onClick={handleBreak}
                disabled={fichadorLoading}
                className={`flex items-center gap-3 px-6 py-4 rounded-xl transition-colors text-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed ${
                  breakMode
                    ? 'bg-yellow-500 text-white hover:bg-yellow-600'
                    : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                }`}
              >
                <Coffee className="w-6 h-6" />
                {breakMode ? 'Fin Descanso' : 'Descanso'}
              </button>
              <button
                onClick={handleClockOut}
                disabled={fichadorLoading}
                className="flex items-center gap-3 px-8 py-4 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-colors text-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <StopCircle className="w-6 h-6" />
                {fichadorLoading ? 'Cargando...' : 'Fichar Salida'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Buscador de registros */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar fichajes (entrada, salida, hora...)"
            value={fichadorSearch}
            onChange={(e) => setFichadorSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-[#3B82F6] focus:ring-2 focus:ring-[#3B82F6]/20"
          />
        </div>
      </div>

      {/* Registros */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900">Registros de hoy</h3>
          <span className="text-sm text-gray-500">{filteredRecords.length} registros</span>
        </div>
        {filteredRecords.length > 0 ? (
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {filteredRecords.map((record, index) => (
              <div key={index} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                <div className="flex items-center gap-3">
                  {record.type === 'entrada' && <PlayCircle className="w-5 h-5 text-green-500" />}
                  {record.type === 'salida' && <StopCircle className="w-5 h-5 text-red-500" />}
                  {record.type === 'inicio_descanso' && <Coffee className="w-5 h-5 text-yellow-500" />}
                  {record.type === 'fin_descanso' && <Coffee className="w-5 h-5 text-green-500" />}
                  <span className="text-gray-700">
                    {record.type === 'entrada' && 'Entrada'}
                    {record.type === 'salida' && 'Salida'}
                    {record.type === 'inicio_descanso' && 'Inicio descanso'}
                    {record.type === 'fin_descanso' && 'Fin descanso'}
                  </span>
                </div>
                <span className="text-gray-500 font-mono">
                  {record.time.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <Clock className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>{fichadorSearch ? 'No hay registros que coincidan' : 'No hay registros hoy'}</p>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="h-full flex flex-col">
      {/* Header - Responsive */}
      <div className="bg-white border-b border-gray-200 px-4 md:px-6 py-3 md:py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 md:gap-3">
            <Users className="w-6 h-6 md:w-8 md:h-8 text-[#3B82F6]" />
            <div>
              <h1 className="text-lg md:text-xl font-semibold text-gray-900">Contactos</h1>
              <p className="text-xs md:text-sm text-gray-500 hidden sm:block">Equipo, contactos y fichador</p>
            </div>
          </div>
        </div>

        {/* Tabs - Responsive con scroll horizontal en móvil */}
        <div className="flex items-center gap-1 mt-3 md:mt-4 border-b border-gray-200 -mb-3 md:-mb-4 -mx-4 md:-mx-6 px-4 md:px-6 overflow-x-auto scrollbar-hide">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 md:gap-2 px-3 md:px-4 py-2.5 md:py-3 text-xs md:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  isActive
                    ? 'border-[#3B82F6] text-[#3B82F6]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span className="hidden xs:inline">{tab.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Content - Responsive padding */}
      <div className="flex-1 overflow-auto p-3 md:p-6 bg-gray-50">
        {activeTab === 'equipo' && renderEquipo()}
        {activeTab === 'contactos' && renderContactos()}
        {activeTab === 'fichador' && renderFichador()}
      </div>

      {/* Modal Nuevo/Editar Contacto */}
      {showNewContact && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowNewContact(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-[500px] max-w-[90vw] overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b bg-gray-50">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingContact ? 'Editar Contacto' : 'Nuevo Contacto'}
              </h3>
              <button onClick={() => setShowNewContact(false)} className="p-1 hover:bg-gray-200 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4 max-h-[60vh] overflow-auto">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Nombre completo"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-[#3B82F6]"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Empresa</label>
                <input
                  type="text"
                  value={formData.company}
                  onChange={(e) => setFormData({...formData, company: e.target.value})}
                  placeholder="Nombre de la empresa"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-[#3B82F6]"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="email@ejemplo.com"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-[#3B82F6]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    placeholder="+34 600 000 000"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-[#3B82F6]"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Categoría</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-[#3B82F6]"
                  >
                    {CATEGORIES.filter(c => c.id !== 'all').map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Dirección</label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                    placeholder="Ciudad, País"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-[#3B82F6]"
                  />
                </div>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.favorite}
                  onChange={(e) => setFormData({...formData, favorite: e.target.checked})}
                  className="w-4 h-4 text-[#3B82F6] rounded"
                />
                <span className="text-sm text-gray-700">Marcar como favorito</span>
              </label>
            </div>
            <div className="flex items-center justify-end gap-3 p-4 border-t bg-gray-50">
              <button
                onClick={() => setShowNewContact(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveContact}
                className="px-4 py-2 bg-[#3B82F6] text-white rounded-lg hover:bg-[#2563EB] transition-colors"
              >
                {editingContact ? 'Guardar' : 'Crear'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
